import { Menu } from '#app/components/Menu/Menu.js';
import { Text } from '#app/components/Primitives/Text.js';
import { getMaybeBrandDetailsFromLink } from '#app/components/ProfileExternalLink/utils.js';
import { type SocialMediaLinkBrand } from '#app/components/SocialMediaLink/types.js';
import { Tooltip } from '#app/components/Tooltip/Tooltip.js';
import { TooltipCompoundProps } from '#app/components/Tooltip/types.js';
import { useAnalytics } from '#app/hooks/useAnalytics.js';
import { useIsMobile } from '#app/hooks/useIsMobile.js';
import { isValidEmail } from '#app/utilities/isValidEmail.js';
import { SocialMediaIcon } from './SocialMediaIcon.js';
import {
  ExternalLinkAltIcon,
  LinkIcon,
  MailSolidIcon,
} from '@contra/icons/icons';
import { type MouseEvent, useCallback, useState } from 'react';
import { css, styled } from 'styled-components';

const ExternalLink = styled.button`
  ${({ theme }) => css`
    display: flex;
    align-items: center;
    color: ${theme.colorsV2.black};

    &:hover {
      cursor: pointer;
    }
  `}
`;

const MenuItem = styled(Menu.Item)`
  ${({ theme }) => css`
    display: flex;
    flex-flow: row nowrap;
    align-items: center;
    justify-content: space-between;
    color: ${theme.colorsV2.black};
    margin-top: 8px;
    width: 100%;
  `}
`;

const TooltipContent = styled.p`
  text-align: left;
  overflow-wrap: break-word;

  & > p {
    margin: 0;
    padding: 0;
  }
`;

const resolveIcon = (
  iconSize: number,
  brandId?: SocialMediaLinkBrand,
  isEmail?: boolean,
) => {
  const iconProps = { 'aria-hidden': true, size: iconSize };
  if (isEmail) {
    return <MailSolidIcon {...iconProps} />;
  }

  if (!brandId) {
    return <ExternalLinkAltIcon {...iconProps} />;
  }

  return (
    <SocialMediaIcon
      {...iconProps}
      brand={brandId}
    />
  );
};

type SocialMediaLinkWithTooltipProps = {
  readonly iconSize?: number;
  readonly label: null | string;
  readonly onClickOverride?: () => void;
  readonly position: number;
  readonly tooltipProps?: Partial<TooltipCompoundProps>;
  readonly url: string;
  readonly visitorCanEdit: boolean;
};

export const SocialMediaLinkWithTooltip = ({
  iconSize = 24,
  label,
  onClickOverride,
  position,
  tooltipProps,
  url,
  visitorCanEdit,
}: SocialMediaLinkWithTooltipProps) => {
  const [linkMenuOpen, setLinkMenuOpen] = useState<boolean>(false);
  const isMobile = useIsMobile();

  const brandDetails = getMaybeBrandDetailsFromLink(url);
  const isEmail = isValidEmail(url.replace('mailto:', ''));

  const { track } = useAnalytics();

  const toggleLinkMenu = useCallback(() => {
    setLinkMenuOpen((currentValue) => !currentValue);
  }, []);

  if (brandDetails === null) {
    // this will only happen if the link is not a valid public URL
    return null;
  }

  const { id: brandId, name: brandName } = brandDetails;
  const resolvedLabel = label ?? brandName;
  const ariaLabelledBy = `${resolvedLabel} link`;
  const ariaLabel = `${resolvedLabel}`;

  const linkProps = {
    'aria-label': ariaLabel,
    href: url,
    id: ariaLabelledBy,
    onClick: (event: MouseEvent<HTMLAnchorElement>) => {
      if (onClickOverride) {
        event.preventDefault();
        onClickOverride();
      } else {
        event.stopPropagation();

        track('link', {
          action: 'tapped',
          component_type: isEmail ? 'email' : resolvedLabel,
          destination_url: url,
          is_owner: visitorCanEdit,
          link_name: 'external_link',
          position_index: position,
        });
      }
    },
    rel: 'noopener nofollow',
    target: '_blank',
  };

  const tooltipContent = (
    <TooltipContent>
      <p>{resolvedLabel}</p>
      <p>{url.replace('mailto:', '')}</p>
    </TooltipContent>
  );
  return isMobile ? (
    <Menu
      isOpen={linkMenuOpen}
      placement="bottom-end"
      toggleMenu={toggleLinkMenu}
    >
      <Menu.Reference>
        <ExternalLink
          aria-label={linkProps['aria-label']}
          id={linkProps.id}
          onClick={toggleLinkMenu}
        >
          {resolveIcon(iconSize, brandId, isEmail)}
        </ExternalLink>
      </Menu.Reference>
      <Menu.Panel>
        <MenuItem
          as="a"
          {...linkProps}
        >
          <div>
            <Text
              color="uiBlackMediumEmphasis"
              textStyle="bodySmall"
            >
              {resolvedLabel}
            </Text>
            <Text textStyle="bodyRegular">{url}</Text>
          </div>
          <LinkIcon size={iconSize} />
        </MenuItem>
      </Menu.Panel>
    </Menu>
  ) : (
    <Tooltip
      content={tooltipContent}
      placement="bottom"
      {...tooltipProps}
    >
      <ExternalLink
        as="a"
        {...linkProps}
      >
        {resolveIcon(iconSize, brandId, isEmail)}
      </ExternalLink>
    </Tooltip>
  );
};
